// @flow

import React, {useRef, useState} from "react";
import {Col, Input} from "antd";
import {useTranslation} from "react-i18next";
import {cities} from "../../../../constants/constants";
import {useFetch} from "../../../../hooks/useFetch";
import {useStore} from "../../../../hooks/useStore";
import {arrayAlphabeticalSort} from "../../../../utils/helpers";
import {CompanyContanier, FilterContainer, SelectField} from "../optionsStyles";
import CompanyShiftOptions from "./Options";
import SubCompanyShiftOptions from "./SubCompanyShiftOptions/SubCompanyShiftOptions";
import { CityFilter } from "../../../_defaultComponents/FilterComponents";

const ShiftOptions = ({companyId}) => {
    const stores = useStore();
    const {t} = useTranslation();

    const [currentPage, setCurrentPage] = useState(1);
    const [city, setCity] = useState("all");
    const [searchText, setSearchText] = useState("");
    const [text, setText] = useState("");
    const [companyRecall, setCompanyRecall] = useState(false);
    const [subCompanyRecall, setSubCompanyRecall] = useState(false);
    const [upperGroup, setUpperGroup] = useState("");
    const [regionGroups, setRegionGroups] = useState([]);
    const [regionGroupsNames, setRegionGroupsNames] = useState([]);

    const companyRecallRef = useRef(companyRecall);
    companyRecallRef.current = companyRecall;
    const subCompanyRecallRef = useRef(subCompanyRecall);
    subCompanyRecallRef.current = subCompanyRecall;

    const [companyLoading, companyData, setCompanyData, setCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.companyStore.getCompany.bind(null, {
            companyId,
            withSubCompanies: false,
            vehicleType: null
        }),
        dependencies: [companyRecall]
    });

    const [subCompanyLoading, subCompanyData, setSubCompanyData, setSubCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn:
            companyData.data && companyData.data._id
                ? stores.companyStore.getSubCompanyList.bind(null, {
                      companyId: companyData.data._id,
                      limit: 10,
                      skip: (currentPage - 1) * 10,
                      activity: "enabled",
                      vehicleType: null,
                      city,
                      searchText,
                      upperGroup: upperGroup,
                      regionGroups: regionGroups?.join(".")
                  })
                : () => [],
        dependencies: [
            companyData,
            currentPage,
            subCompanyRecall,
            city,
            searchText,
            upperGroup,
            regionGroups
        ]
    });

    const [upperGroupsLoading, upperGroups, setUpperGroups, setUpperGroupsLoading] = useFetch({
        fetchFn: stores.regionStore.getUpperGroupsByCompanyIdAndCity.bind(null, {
            companyId,
            city
        }),
        dependencies: [companyId, city],
        initialCall: false
    });

    return (
        <div data-testid="appointmentOptionsFilter">
            <h2 data-testid="companyName">{companyData?.data?.companyName}</h2>
            <CompanyContanier>
                <CompanyShiftOptions
                    companyLoading={companyLoading}
                    setCompanyLoading={setCompanyLoading}
                    companyData={companyData}
                    searchText={searchText}
                    city={city}
                    upperGroup={upperGroup}
                    regionGroups={regionGroups}
                    setSubCompanyRecall={setSubCompanyRecall}
                    setCompanyRecall={setCompanyRecall}
                    regionGroupsNames={regionGroupsNames}
                    companyRecallRef={companyRecallRef}
                    subCompanyRecallRef={subCompanyRecallRef}
                />
            </CompanyContanier>
            <Col span={12} data-testid="filterCard">
                <FilterContainer type="flex" gutter={[20, 20]}>
                    <Col span={6}>
                        <CityFilter
                            value={city}
                            onChange={value => {
                                setCurrentPage(1);
                                setCity(value);
                                setRegionGroups([]);
                                setRegionGroupsNames([]);
                            }}
                        />
                    </Col>
                    <Col span={6}>
                        <h5>{t("fields.upperGroup", {ns: "common"})}</h5>
                        <SelectField
                            value={upperGroup}
                            onChange={value => {
                                setCurrentPage(1);
                                setUpperGroup(value);
                            }}
                            disabled={!city}
                            notFoundContent={t("filters.dataNotFound", {ns: "common"})}
                            placeholder={t("filters.addFilter", {ns: "common"})}
                            optionFilterProp="children"
                            data-testid="upperRegionSelect"
                            filterOption={(input, option) =>
                                option.children
                                    .toString()
                                    .toLowerCase()
                                    .indexOf(input.toLowerCase()) >= 0
                            }
                            filterSort={(optionA, optionB) =>
                                optionA.children
                                    .toString()
                                    .toLowerCase()
                                    .localeCompare(optionB.children.toString().toLowerCase())
                            }
                        >
                            <SelectField.Option value="" data-testid="upperRegionSelect_option">
                                {t("filters.all", {ns: "common"})}
                            </SelectField.Option>
                            {upperGroups.map(upperGroup => (
                                <SelectField.Option
                                    key={upperGroup.group}
                                    value={upperGroup.group}
                                    data-testid="upperRegionSelect_option"
                                >
                                    {upperGroup.group}
                                </SelectField.Option>
                            ))}
                        </SelectField>
                    </Col>
                    <Col span={6}>
                        <h5>{t("fields.regionGroup", {ns: "common"})}</h5>
                        <SelectField
                            value={regionGroups}
                            onChange={(value, children) => {
                                setCurrentPage(1);
                                setRegionGroups(value);
                                setRegionGroupsNames(children);
                            }}
                            disabled={!city || upperGroup === ""}
                            notFoundContent={t("filters.dataNotFound", {ns: "common"})}
                            mode="multiple"
                            placeholder={t("filters.addFilter", {ns: "common"})}
                            optionFilterProp="children"
                            data-testid="regionGroupsSelect"
                            filterOption={(input, option) =>
                                option.children
                                    .toString()
                                    .toLowerCase()
                                    .indexOf(input.toLowerCase()) >= 0
                            }
                            filterSort={(optionA, optionB) =>
                                optionA.children
                                    .toString()
                                    .toLowerCase()
                                    .localeCompare(optionB.children.toString().toLowerCase())
                            }
                        >
                            {upperGroups
                                .find(c => c.group == upperGroup)
                                ?.regiongroups.map(regionGroupItem => (
                                    <SelectField.Option
                                        key={regionGroupItem._id}
                                        value={regionGroupItem._id}
                                        data-testid="regionGroupsSelect_option"
                                    >
                                        {regionGroupItem.name}
                                    </SelectField.Option>
                                ))}
                        </SelectField>
                    </Col>
                    <Col span={6}>
                        <h5>{t("filters.search", {ns: "common"})}</h5>
                        <Input.Search
                            value={text}
                            data-testid="searchInput"
                            onSearch={value => {
                                setCurrentPage(1);
                                setSearchText(value);
                            }}
                            onChange={event => {
                                setText(event.target.value);
                            }}
                        ></Input.Search>
                    </Col>
                </FilterContainer>
            </Col>
            <SubCompanyShiftOptions
                subCompanyData={subCompanyData}
                subCompanyLoading={subCompanyLoading}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                companyId={companyId}
                subCompanyRecallRef={subCompanyRecallRef}
                setSubCompanyRecall={setSubCompanyRecall}
                setSubCompanyData={setSubCompanyData}
            />
        </div>
    );
};

export default ShiftOptions;
